import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, brandingApi } from '../api';
import { Lock, Sparkles } from 'lucide-react';

export default function Login() {
  const { signIn, theme, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [step, setStep] = useState('select');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [hasBanner, setHasBanner] = useState(false);
  // iter45 — daily rotation: pick a slot in the uploaded gallery based on
  // today's IST calendar date so every visitor on the same day sees the
  // same banner (predictable + CDN-cache-friendly).
  const [bannerIndex, setBannerIndex] = useState(0);

  useEffect(() => { if (user) navigate('/', { replace: true }); }, [user, navigate]);

  // iter43/45/46 — fetch banner status pre-auth. Server now picks the active
  // slot (respecting Banner Scheduler pins), so we just honour its choice.
  useEffect(() => {
    brandingApi.status().then(r => {
      const count = Number(r?.count || 0);
      if (count > 0) {
        const idx = Number.isInteger(r?.activeIndex) ? r.activeIndex : 0;
        setBannerIndex(Math.max(0, Math.min(count - 1, idx)));
        setHasBanner(true);
      } else {
        setHasBanner(false);
      }
    }).catch(() => setHasBanner(false));
  }, []);

  const effectiveEmail = customEmail.trim() || email;

  const send = async () => {
    setErr(''); setLoading(true);
    try {
      await authApi.requestOtp(effectiveEmail);
      setStep('otp');
      setEmail(effectiveEmail);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not send OTP');
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setErr(''); setLoading(true);
    try {
      const r = await authApi.verifyOtp(effectiveEmail, otp.trim());
      signIn(r.user, r.token);
      navigate('/');
    } catch (e) {
      setErr(e.response?.data?.detail || 'OTP verification failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#F2EFE7]">
      {/* Left brand panel (hidden < md) — either custom banner or brand-colour monogram */}
      <div className="hidden md:block md:w-[46%] lg:w-[52%] relative overflow-hidden">
        {hasBanner ? (
          <>
            <img
              src={brandingApi.bannerUrl(null, bannerIndex)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="login-hero-banner"
              onError={() => setHasBanner(false)}
            />
            {/* Legibility scrim so the copy still reads on light imagery */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)' }} />
          </>
        ) : (
          <>
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(160deg, #7A2E41 0%, #9C3D54 55%, #5A1F30 100%)',
              }}
            />
            {/* Subtle brand monogram behind the copy */}
            <div
              className="absolute right-[-80px] bottom-[-120px] font-serif select-none pointer-events-none"
              style={{ fontSize: '520px', lineHeight: 1, color: 'rgba(255,255,255,0.05)', letterSpacing: '-0.05em' }}
              aria-hidden="true"
            >CF</div>
          </>
        )}
        <div className="relative z-10 h-full flex flex-col justify-between p-10 lg:p-14 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-[12px] bg-white/95 text-[#9C3D54] flex items-center justify-center font-bold text-[14px] tracking-[0.6px]">CF</div>
            <div>
              <div className="font-serif text-[26px] leading-none tracking-[0.5px]">CORE FUTURE</div>
              <div className="text-[10px] tracking-[0.18em] uppercase mt-1 opacity-80">Tester Requisition Portal</div>
            </div>
          </div>

          <div className="max-w-[440px]">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase mb-4 opacity-85">
              <Sparkles size={14} /> Internal · trusted · secure
            </div>
            <h2 className="font-serif text-[40px] lg:text-[48px] leading-[1.05]">Every tester,<br/>on the record.</h2>
            <p className="mt-4 text-[14.5px] leading-relaxed opacity-90">
              Requisition, fulfilment and dispatch of every tester unit across our EBOs — fully audited, per-SKU per-branch.
            </p>
          </div>

          <div className="text-[11px] tracking-[0.14em] uppercase opacity-70">© FUTURE MAKEUP INDIA PVT LTD · Internal use only</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[400px]">
          <div className="md:hidden flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-[14px] flex items-center justify-center text-white font-bold tracking-[0.6px]" style={{ background: theme.primary }}>CF</div>
            <h1 className="font-serif text-[28px] mt-3 tracking-[0.5px]" style={{ color: theme.primary }}>CORE FUTURE</h1>
            <p className="text-[10.5px] tracking-[0.16em] uppercase text-[#7a6f5c] mt-1">Tester Requisition Portal</p>
          </div>

          <div className="hidden md:block mb-6">
            <h1 className="font-serif text-[30px] text-[#3a1d24]">Sign in</h1>
            <div className="flex items-center gap-2 mt-1 text-[11px] tracking-[0.14em] text-[#7a6f5c] uppercase">
              <span>CORE FUTURE Portal</span>
              <span>·</span>
              <span className="normal-case tracking-normal">post.futuremakeup.com</span>
              <span>·</span>
              <Lock size={11} className="text-[#B78140]" />
            </div>
          </div>

          <div className="fm-card p-5">
            {step === 'select' && (
              <>
                <label className="block text-[13px] text-[#232323] mb-2 font-medium">Sign in with your work email</label>
                <input
                  type="email"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="you@futuremakeup.in"
                  autoComplete="email"
                  autoFocus
                  className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2.5 text-[14px] bg-white"
                  disabled={loading}
                  data-testid="login-email-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && effectiveEmail && !loading) send(); }}
                />
                <p className="text-[12px] text-[#7a6f5c] mt-3 leading-relaxed">
                  We'll email a 6-digit one-time password. It expires in 10 minutes.
                </p>
                {err && <div className="text-[12.5px] text-[#a04747] mt-2" data-testid="login-error">{err}</div>}
                <button
                  disabled={!effectiveEmail || loading}
                  onClick={send}
                  data-testid="login-send-otp-btn"
                  className="mt-4 w-full py-2.5 rounded-md text-white font-medium text-[14px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: effectiveEmail ? theme.primary : '#E5D6DA' }}
                >{loading ? 'Sending…' : 'Send one-time password'}</button>
              </>
            )}

            {step === 'otp' && (
              <>
                <p className="text-[13px] text-[#232323] mb-3 leading-relaxed">
                  OTP sent to <span className="font-semibold">{email}</span>. Check your inbox (and spam folder if needed) — the code expires in 10 minutes.
                </p>
                <label className="block text-[13px] text-[#232323] mb-2 font-medium">Enter OTP</label>
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[16px] bg-white font-mono tracking-[8px] text-center"
                />
                {err && <div className="text-[12.5px] text-[#a04747] mt-2">{err}</div>}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => { setStep('select'); setErr(''); setOtp(''); }}
                    className="px-4 py-2 border border-[#E7E2D3] rounded-md text-[14px] font-medium hover:bg-[#F7F4EA] transition-colors"
                    disabled={loading}
                  >Back</button>
                  <button
                    onClick={verify}
                    disabled={!otp || loading}
                    className="flex-1 py-2 rounded-md text-white font-medium text-[14px] disabled:opacity-60"
                    style={{ background: theme.primary }}
                  >{loading ? 'Verifying…' : 'Verify & sign in'}</button>
                </div>
                <button
                  onClick={send}
                  className="mt-3 text-[12.5px] underline text-[#7a6f5c] hover:text-[#232323]"
                  disabled={loading}
                >Resend OTP</button>
              </>
            )}
          </div>

          <p className="text-center text-[11px] text-[#7a6f5c] mt-6 md:hidden">FUTURE MAKEUP INDIA PVT LTD · Internal use only</p>
        </div>
      </div>
    </div>
  );
}
